/**
 * The service worker talking to the running app.
 *
 * **Three complaints from one learner, and two of them are this file's absence.** She was
 * scrolled up reading a briefing, a question arrived, she tapped the notification, *nothing
 * happened*, and the question did not appear in the thread until she navigated away and back.
 *
 * The worker could already show a notification and focus the app. What it could not do is say
 * anything to the app once it was focused, so:
 *
 * - **A push did not refresh an open thread.** The chat polls every twenty seconds and
 *   refreshes on `visibilitychange`, which covers an app coming back from the background — but
 *   an app that is *already* on screen fires no visibility change when a push lands, so a
 *   learner looking straight at the thread waited out the poll. Twenty seconds after being
 *   told a question had arrived, with the question nowhere on screen, is indistinguishable
 *   from it being lost. Navigating away and back is what she found instead, which is the
 *   product teaching somebody a workaround.
 * - **Tapping a notification could only ever reload.** `client.navigate()` throws the app away
 *   and rebuilds it — losing her scroll position, which on a thread she was reading back is
 *   precisely the thing worth keeping — and on iOS standalone it is not dependable at all.
 *
 * So the worker now posts a message and the app decides what to do with it, which is both
 * instant and non-destructive. The worker keeps `navigate` only as the fallback for when there
 * is no live client to talk to.
 *
 * **A module-level registry rather than React context**, because the subscriber is `Chat` and
 * the router is in `App`, they are far apart in the tree, and the thing being shared is a
 * browser event source that exists exactly once per document. A provider would be a second
 * mechanism for a singleton the platform already gives us.
 */

/** What the worker sends. Mirrors the `postMessage` calls in `public/sw.js`. */
export interface SwMessage {
  /**
   * `push` — a message arrived while the app was running. Refresh, do not navigate: she may
   * be reading something else, and yanking the screen out from under a reader is the bug this
   * whole chat screen was rebuilt over.
   *
   * `open` — a notification was tapped. This one is an instruction, because she asked for it.
   */
  type: 'push' | 'open';
  /** Where the message lives, e.g. `/chat`. Only acted on for `open`. */
  path?: string;
  messageId?: string;
}

type Listener = (message: SwMessage) => void;

const listeners = new Set<Listener>();

/**
 * Subscribes to what the worker says, and returns the unsubscribe.
 *
 * No capability check here and nothing touched that only a browser has: the DOM half lives in
 * `listenForWorkerMessages` (`push.ts`), for `chat/keyboard.ts`'s reason — a decision that
 * cannot be tested is one that gets argued about instead, and this one is only reachable on a
 * phone with a real push. Where there is no service worker this simply subscribes to a source
 * that never fires.
 */
export function onServiceWorkerMessage(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Hands a message to every subscriber. Called by the browser wiring, and by tests.
 *
 * **One throwing subscriber must not rob the others.** `Chat` refreshes off this and `App`
 * navigates off it; if a failed refresh stopped the navigation, a tapped notification would
 * once again do nothing, which is the complaint this channel exists to answer.
 */
export function deliverSwMessage(message: SwMessage): void {
  for (const listener of listeners) {
    try {
      listener(message);
    } catch {
      // Nothing to do: a subscriber that cannot handle a refresh is not a reason to drop the
      // navigation, and there is no user-facing failure worth reporting.
    }
  }
}

/**
 * Narrows whatever arrived on the channel, or `undefined` if it is not ours.
 *
 * The payload originates in a push from our own server, but it has been through the browser's
 * messaging channel and a worker that may be weeks old — service workers live in browsers for
 * weeks — so only the three fields this app acts on are carried through, and an unrecognised
 * `type` is dropped rather than forwarded. A listener that spread whatever arrived into router
 * state would be one shape change away from navigating somewhere nobody meant.
 */
export function readSwMessage(data: unknown): SwMessage | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const raw = data as Partial<SwMessage>;
  if (raw.type !== 'push' && raw.type !== 'open') return undefined;
  return {
    type: raw.type,
    ...(typeof raw.path === 'string' ? { path: raw.path } : {}),
    ...(typeof raw.messageId === 'string' ? { messageId: raw.messageId } : {}),
  };
}
